import { yupResolver } from '@hookform/resolvers/yup'
import { useQueryClient } from '@tanstack/react-query'
import axios from 'axios'
import ThaiDatePicker from 'common/components/ThaiDatePicker'
import { getPondKey } from 'common/constants/queryKeys'
import {
  formatDate,
  formatThaiShortDateTwoDigitYear,
  formatUTCDateTime,
  getDateFromInputDateTime,
} from 'common/utils/date'
import { fInteger } from 'common/utils/formatNumber'
import { labelWithSpecies } from 'common/utils/species'
import { ErrorEnumMessage } from 'core/api/constants/error_messages'
import { endOfToday, format, isValid } from 'date-fns'
import { useInvalidateCostQueries } from 'modules/cost/api/invalidate'
import { useFarms } from 'modules/farm/api/queries'
import { useGetCompletedPonds, useGetPondDashboard } from 'modules/pond/api/queries'
import {
  useGetPondNameById,
  useIsFishSpecie,
  useSpeciesId,
  useSpeciesName,
} from 'modules/pond/api/selector'
import BaseRecordDialog from 'modules/pond/components/BaseRecordDialog'
import { SpeciesName } from 'modules/pond/constants'
import { useMutateCreateShrimpTransfer } from 'modules/shrimpTransfer/api/mutation'
import { useGetLatestCostPerKg, useGetTransferRecentSize } from 'modules/shrimpTransfer/api/queries'
import { DestinationSelection } from 'modules/shrimpTransfer/api/types'
import SpeciesIcon from 'modules/shrimpTransfer/components/SpeciesIcon'
import {
  estimateTransfer,
  netRemainingCount,
  parseTransferErrorCode,
} from 'modules/shrimpTransfer/utils/transferEstimate'
import { useSnackbar } from 'notistack'
import { ReactNode, useEffect, useRef, useState } from 'react'
import { Control, useForm, Controller } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import * as Yup from 'yup'

import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined'
import CheckIcon from '@mui/icons-material/Check'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import {
  Button,
  DialogContent,
  IconButton,
  InputAdornment,
  Popover,
  Stack,
  TextField,
  Box,
  Typography,
} from '@mui/material'
import { TimePicker } from '@mui/x-date-pickers'

import DestinationPondPicker, { PickerReopenRequest } from '../DestinationPondPicker'
import TransferConfirmDialog from '../TransferConfirmDialog'
import TransferFormRow from '../TransferFormRow'
import TransferIcon from '../TransferIcon'

type FormValues = {
  transfer_date: Date
  transfer_time: Date
  destination_pond_id: string
  amount: string | number
  cost_per_kg: string | number
  amount_per_kg: string | number
}
interface ShrimpTransferRecordDialogProps {
  onClose: () => void
  open: boolean
}

const EMPTY_VALUES: FormValues = {
  transfer_date: new Date(),
  transfer_time: new Date(),
  destination_pond_id: '',
  amount: '',
  cost_per_kg: '',
  amount_per_kg: '',
}

const toPositive = (value: string | number) => {
  const parsed = Number(value)
  return value !== '' && Number.isFinite(parsed) && parsed > 0 ? parsed : 0
}

const formatKg = (kg: number) => {
  // Whole numbers without decimals, otherwise up to 2 decimals.
  return Number.isInteger(kg) ? fInteger(kg) : kg.toFixed(2)
}

interface EditableValueRowProps {
  name: 'amount_per_kg' | 'cost_per_kg'
  control: Control<FormValues>
  icon: ReactNode
  label: string
  unit: string
  editLabel: string
  confirmLabel: string
  error?: string
  onUserChange?: () => void
}

/** Read-only "value unit" line with a pencil; the pencil swaps it for an input with a confirm tick. */
const EditableValueRow = ({
  name,
  control,
  icon,
  label,
  unit,
  editLabel,
  confirmLabel,
  error,
  onUserChange,
}: EditableValueRowProps) => {
  const [editing, setEditing] = useState(false)

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => {
        const empty = field.value === '' || field.value === null || field.value === undefined
        // Nothing to show yet (no prefill): go straight to the input.
        const isEditing = editing || empty
        const confirm = () => {
          field.onBlur()
          setEditing(false)
        }
        return (
          <TransferFormRow
            icon={icon}
            label={label}
            action={
              isEditing ? (
                <IconButton aria-label={confirmLabel} onClick={confirm} disabled={empty}>
                  <CheckIcon />
                </IconButton>
              ) : (
                <IconButton aria-label={editLabel} onClick={() => setEditing(true)}>
                  <TransferIcon name="edit" size={2.5} />
                </IconButton>
              )
            }
          >
            {isEditing ? (
              <TextField
                {...field}
                // Typing keeps the input open even when it started empty.
                onChange={(event) => {
                  setEditing(true)
                  onUserChange?.()
                  field.onChange(event)
                }}
                autoFocus={editing}
                fullWidth
                size="small"
                margin="none"
                type="number"
                inputProps={{ 'aria-label': label }}
                InputProps={{
                  endAdornment: <InputAdornment position="end">{unit}</InputAdornment>,
                  inputMode: 'decimal',
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    // Confirms the edit, never submits the whole form.
                    event.preventDefault()
                    if (!empty) confirm()
                  }
                }}
                error={!!error}
                helperText={error ?? ''}
              />
            ) : (
              <>
                <Typography fontWeight={700}>{`${Number(field.value)} ${unit}`}</Typography>
                {error && (
                  <Typography variant="caption" color="error">
                    {error}
                  </Typography>
                )}
              </>
            )}
          </TransferFormRow>
        )
      }}
    />
  )
}

const ShrimpTransferRecordDialog = ({ onClose, open }: ShrimpTransferRecordDialogProps) => {
  const { t } = useTranslation(['shrimpTransfer', 'common'])
  const { farmId = '', pondId = '' } = useParams()
  const species = useSpeciesName()
  const speciesId = useSpeciesId()
  const isFish = useIsFishSpecie()
  const { enqueueSnackbar } = useSnackbar()
  const queryClient = useQueryClient()
  const invalidateCostQueries = useInvalidateCostQueries()
  const { data: sourcePond } = useGetPondNameById(pondId)
  const { data: farmData } = useFarms()
  const { data: dashboard } = useGetPondDashboard()
  // Net count left to move (stocked - dead - sold), shown next to the weight header.
  const netRemaining = netRemainingCount(dashboard)
  const validationSchema = Yup.object().shape({
    transfer_date: Yup.date().required(t('transfer.required_date', { species })),
    transfer_time: Yup.date().required(t('transfer.required_time')),
    destination_pond_id: Yup.string().required(t('transfer.required_destination')),

    amount: Yup.number()
      .typeError(t('transfer.required_quantity'))
      .positive(t('transfer.positive_quantity'))
      .required(t('transfer.required_quantity')),

    cost_per_kg: Yup.number()
      .typeError(t('transfer.required_cost', { species }))
      .positive(t('transfer.positive_cost', { species }))
      .required(t('transfer.required_cost', { species })),

    amount_per_kg: Yup.number()
      .typeError(t('transfer.required_shrimp_size', { species }))
      .positive(t('transfer.positive_shrimp_size', { species }))
      .required(t('transfer.required_shrimp_size', { species })),
  })

  const {
    control,
    handleSubmit,
    formState: { errors, isDirty },
    reset,
    setValue,
    watch,
  } = useForm<FormValues>({
    defaultValues: EMPTY_VALUES,
    resolver: yupResolver(validationSchema),
    mode: 'onBlur',
  })

  const [destination, setDestination] = useState<DestinationSelection | null>(null)
  const [reopenRequest, setReopenRequest] = useState<PickerReopenRequest>()
  const [pendingData, setPendingData] = useState<FormValues | null>(null)
  const [dateOpen, setDateOpen] = useState(false)
  // Until the user touches the date or time it is still "now".
  const [dateTouched, setDateTouched] = useState(false)
  const dateRowRef = useRef<HTMLElement | null>(null)

  const { data: latestCostPerKg, refetch: refetchLatestCostPerKg } = useGetLatestCostPerKg()
  const latestCostPrefilledRef = useRef(false)
  // Date string the user last typed a size for; a new date prefills again.
  const sizeEditedForDateRef = useRef<string | null>(null)

  const createShrimpTransferMutation = useMutateCreateShrimpTransfer()

  const farmNameOf = (id: string) => farmData?.farms.find((farm) => farm.id === id)?.name ?? ''
  const currentFarmName = farmNameOf(farmId)

  const [amountValue, sizeValue, costValue, destinationPondId, dateValue, timeValue] = watch([
    'amount',
    'amount_per_kg',
    'cost_per_kg',
    'destination_pond_id',
    'transfer_date',
    'transfer_time',
  ])
  const estimate = estimateTransfer({
    isFish,
    weightKg: toPositive(amountValue),
    size: toPositive(sizeValue),
    cost: toPositive(costValue),
  })
  const canSubmit =
    !!destinationPondId &&
    toPositive(amountValue) > 0 &&
    toPositive(sizeValue) > 0 &&
    toPositive(costValue) > 0

  const estimateText = estimate
    ? t('transfer.estimateSummary', {
        amount: fInteger(estimate.amount),
        value: fInteger(estimate.value),
      })
    : t('transfer.estimateIncomplete')

  const transferDateTime = getDateFromInputDateTime(dateValue, timeValue)
  const sizeDate = isValid(dateValue) ? formatDate(dateValue) : undefined
  const { data: recentSize } = useGetTransferRecentSize(sizeDate, open)
  // A cleared / half-typed picker value is not a valid date yet.
  const dateText = isValid(transferDateTime)
    ? `${formatThaiShortDateTwoDigitYear(transferDateTime)} · ${format(transferDateTime, 'HH:mm')}`
    : '-'
  const isCrossFarm = !!destination && destination.farmId !== farmId
  const sizeUnit = t(species === SpeciesName.Fish ? 'transfer.gram' : 'transfer.amountPerKg')
  const costUnit = t(isFish ? 'transfer.bahtPerPiece' : 'transfer.bahtPerKg')

  // Auto-select the first empty pond of the current farm once per form open,
  // and only while the user has not chosen a destination themselves.
  const destinationResolvedRef = useRef(false)
  const {
    data: emptyPondsData,
    isSuccess: isEmptyPondsLoaded,
    isError: isEmptyPondsError,
  } = useGetCompletedPonds({
    farmId,
    excludePondId: pondId,
    speciesId: speciesId || undefined,
    enabled: open,
  })
  const firstEmptyPond = emptyPondsData?.ponds?.[0]

  const handleSelectDestination = (selection: DestinationSelection) => {
    destinationResolvedRef.current = true
    setDestination(selection)
    setValue('destination_pond_id', selection.pondId, { shouldValidate: true, shouldDirty: true })
  }

  const clearDestination = () => {
    setDestination(null)
    setValue('destination_pond_id', '', { shouldDirty: true })
  }

  const showError = (message: string) => enqueueSnackbar(message, { variant: 'error' })

  const handleSubmitError = (error: unknown) => {
    if (!axios.isAxiosError(error)) {
      // Not an HTTP failure (a bug on our side): never claim anything about the save.
      showError(t('common:error.generic'))
      return
    }
    const response = error.response
    if (!response) {
      // Timeout / network error: the request may have reached the server.
      showError(t('transfer.error.uncertain'))
      return
    }
    const code = parseTransferErrorCode(response.data?.message)
    switch (code) {
      case 'DESTINATION_POND_NOT_AVAILABLE':
        showError(t('transfer.error.pondNotAvailable'))
        // Re-open on the farm the stale pond belonged to.
        setReopenRequest((prev) => ({ id: (prev?.id ?? 0) + 1, farmId: destination?.farmId }))
        clearDestination()
        queryClient.invalidateQueries({ queryKey: [...getPondKey.all(), 'completed'] })
        break
      case 'NO_PERMISSION_ON_DESTINATION_FARM':
      case 'DESTINATION_FARM_NOT_IN_ORGANIZATION':
        showError(t('transfer.error.noPermissionFarm', { farm: destination?.farmName ?? '' }))
        break
      case 'NOT_ENOUGH_SHRIMP': {
        // Net stocked - dead - sold: the count the backend checked against.
        showError(
          typeof netRemaining === 'number'
            ? t('transfer.error.notEnough', {
                pond: sourcePond?.name ?? '',
                remaining: fInteger(netRemaining),
              })
            : t('transfer.not_enough_shrimp', { species })
        )
        break
      }
      case 'DESTINATION_POND_SPECIES_MISMATCH':
        showError(t('transfer.error.speciesMismatch'))
        break
      case 'DESTINATION_FARM_NOT_FOUND':
        showError(t('transfer.error.farmNotFound'))
        break
      default: {
        // The create request is silent in the interceptor (mutation.ts), so
        // reproduce what it would have shown: the translated 409 message when
        // the code is known, "forbidden" for a 403, otherwise generic.
        const rawCode = String(response.data?.message ?? '')
          .split(':')
          .pop()
          ?.trim()
        const mapped = rawCode
          ? ErrorEnumMessage[rawCode as keyof typeof ErrorEnumMessage]
          : undefined
        if (mapped) showError(mapped)
        else if (response.status === 403) showError(t('common:error.forbidden'))
        else showError(t('common:error.generic'))
      }
    }
  }

  const submitTransfer = async (data: FormValues) => {
    const transferDateTime = getDateFromInputDateTime(data.transfer_date, data.transfer_time)

    const costData = {
      transfer_date: formatUTCDateTime(transferDateTime),
      amount: Number(data.amount),
      cost_per_kg: Number(data.cost_per_kg),
      destination_pond_id: data.destination_pond_id,
      amount_per_kg: Number(data.amount_per_kg),
    }

    try {
      await createShrimpTransferMutation.mutateAsync({
        body: costData,
        destinationFarmId: destination?.farmId,
      })
      setPendingData(null)
      invalidateCostQueries()
      onClose()
    } catch (error: unknown) {
      setPendingData(null)
      handleSubmitError(error)
    }
  }

  // Every transfer is confirmed first (same farm too).
  const onSubmit = async (data: FormValues) => {
    setPendingData(data)
  }

  useEffect(() => {
    if (open) {
      refetchLatestCostPerKg()
    } else {
      latestCostPrefilledRef.current = false
      destinationResolvedRef.current = false
      sizeEditedForDateRef.current = null
      // Start every new record from a blank form (also drops the old destination).
      reset({ ...EMPTY_VALUES, transfer_date: new Date(), transfer_time: new Date() })
      setDestination(null)
      setPendingData(null)
      setDateOpen(false)
      setDateTouched(false)
    }
  }, [open, refetchLatestCostPerKg, reset])

  useEffect(() => {
    if (!open || destinationResolvedRef.current) return
    if (!isEmptyPondsLoaded && !isEmptyPondsError) return
    destinationResolvedRef.current = true
    if (!firstEmptyPond) return
    setDestination({
      pondId: firstEmptyPond.id,
      pondName: firstEmptyPond.name,
      farmId,
      farmName: currentFarmName,
    })
    // Not marked dirty: a prefill must not block the cost-per-kg prefill.
    setValue('destination_pond_id', firstEmptyPond.id)
  }, [
    open,
    isEmptyPondsLoaded,
    isEmptyPondsError,
    firstEmptyPond,
    farmId,
    currentFarmName,
    setValue,
  ])

  useEffect(() => {
    if (!open || latestCostPrefilledRef.current || isDirty) return
    if (!latestCostPerKg?.cost_per_kg) return
    latestCostPrefilledRef.current = true
    const roundedCostPerKg = Math.round(latestCostPerKg.cost_per_kg * 100) / 100
    reset((values) => ({ ...values, cost_per_kg: roundedCostPerKg }))
  }, [open, latestCostPerKg, reset, isDirty])

  // Size follows the selected transfer date (latest sampling on or before it);
  // empty when there is none. A manual edit holds until the date changes again.
  useEffect(() => {
    if (!open || recentSize === undefined) return
    if (sizeEditedForDateRef.current === sizeDate) return
    setValue('amount_per_kg', recentSize.size ?? '')
  }, [open, recentSize, sizeDate, setValue])

  return (
    <>
      <BaseRecordDialog
        onClose={onClose}
        open={open}
        formId="shrimp-transfer"
        title={`${labelWithSpecies(t('transfer.records'), species)} · ${t('transfer.pondLabel', {
          name: sourcePond?.name ?? '',
        })}`}
        isShowPondName={false}
        isSubmitting={createShrimpTransferMutation.isLoading}
        disabledSubmit={!canSubmit}
        submitButtonLabel={
          destination
            ? t('transfer.saveMoveTo', {
                pond: t('transfer.pondLabel', { name: destination.pondName }),
              })
            : undefined
        }
        submitButtonSx={{
          bgcolor: 'action.primaryBg',
          color: 'action.primaryOn',
          '&:hover': { bgcolor: 'action.primaryHover' },
          '&:active': { bgcolor: 'action.primaryPressed' },
        }}
        enableCloseButton
        fullWidth
      >
        <form id="shrimp-transfer" onSubmit={handleSubmit(onSubmit)}>
          <DialogContent sx={{ px: 0, pt: 0 }}>
            <Box sx={{ borderTop: 1, borderColor: 'divider' }}>
              <TransferFormRow
                ref={dateRowRef}
                icon={<CalendarMonthOutlinedIcon />}
                label={t('transfer.dateLabel')}
                onClick={() => setDateOpen(true)}
                action={<ChevronRightIcon />}
                ariaProps={{ 'aria-haspopup': 'dialog', 'aria-expanded': dateOpen }}
              >
                <Typography fontWeight={700} data-testid="transfer-date-value">
                  {dateTouched ? dateText : `${t('transfer.now')} · ${dateText}`}
                </Typography>
                {(errors.transfer_date || errors.transfer_time) && (
                  <Typography variant="caption" color="error">
                    {errors.transfer_date?.message ?? errors.transfer_time?.message}
                  </Typography>
                )}
              </TransferFormRow>
              <Popover
                open={dateOpen}
                anchorEl={dateRowRef.current}
                onClose={() => setDateOpen(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              >
                <Stack spacing={2} sx={{ p: 2 }}>
                  <Stack direction="row" spacing={2}>
                    <Controller
                      name="transfer_date"
                      control={control}
                      render={({ field }) => (
                        <ThaiDatePicker
                          label={t('transfer.dateDay')}
                          format="dd/MM/yyyy"
                          value={field.value}
                          onChange={(value, context) => {
                            setDateTouched(true)
                            field.onChange(value, context)
                          }}
                          maxDate={endOfToday()}
                          slotProps={{
                            textField: {
                              error: !!errors.transfer_date,
                              helperText: errors.transfer_date?.message,
                              required: true,
                            },
                          }}
                        />
                      )}
                    />
                    <Controller
                      name="transfer_time"
                      control={control}
                      render={({ field }) => (
                        <TimePicker
                          label={t('transfer.timeDay')}
                          ampm={false}
                          value={field.value}
                          onChange={(value) => {
                            setDateTouched(true)
                            field.onChange(value)
                          }}
                          slotProps={{
                            textField: {
                              error: !!errors.transfer_time,
                              helperText: errors.transfer_time?.message,
                              required: true,
                            },
                          }}
                        />
                      )}
                    />
                  </Stack>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                      variant="contained"
                      onClick={() => setDateOpen(false)}
                      sx={{
                        bgcolor: 'action.primaryBg',
                        color: 'action.primaryOn',
                        '&:hover': { bgcolor: 'action.primaryHover' },
                        '&:active': { bgcolor: 'action.primaryPressed' },
                      }}
                    >
                      {t('common:confirm')}
                    </Button>
                  </Box>
                </Stack>
              </Popover>

              <Box>
                <DestinationPondPicker
                  value={destination}
                  onChange={handleSelectDestination}
                  currentFarmId={farmId}
                  sourcePondId={pondId}
                  speciesId={speciesId || undefined}
                  error={!!errors.destination_pond_id}
                  helperText={errors.destination_pond_id?.message}
                  reopenRequest={reopenRequest}
                  label={t('transfer.moveToPond')}
                />
              </Box>

              <EditableValueRow
                name="amount_per_kg"
                control={control}
                icon={<TransferIcon name="ruler" size={2.5} />}
                label={t('transfer.shrimpSize', { species })}
                unit={sizeUnit}
                editLabel={t('transfer.editValue')}
                confirmLabel={t('transfer.confirmValue')}
                error={errors.amount_per_kg?.message}
                onUserChange={() => {
                  sizeEditedForDateRef.current = sizeDate ?? null
                }}
              />
              <EditableValueRow
                name="cost_per_kg"
                control={control}
                icon={<TransferIcon name="wallet" size={2.5} />}
                label={isFish ? t('transfer.cost.per.one') : t('transfer.cost.per.kg')}
                unit={costUnit}
                editLabel={t('transfer.editValue')}
                confirmLabel={t('transfer.confirmValue')}
                error={errors.cost_per_kg?.message}
              />
            </Box>

            <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 3, mb: 1 }}>
              <SpeciesIcon isFish={isFish} size={3} />
              <Typography fontWeight={700}>{t('transfer.quantity')}</Typography>
              {netRemaining !== undefined && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ flex: 1, textAlign: 'right' }}
                >
                  {t('transfer.canMoveMore', { remaining: fInteger(netRemaining) })}
                </Typography>
              )}
            </Stack>
            <Controller
              name="amount"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  margin="none"
                  inputProps={{ 'aria-label': t('transfer.quantity') }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Typography variant="h6" fontWeight={700} color="text.primary">
                          {t('transfer.kg')}
                        </Typography>
                      </InputAdornment>
                    ),
                    inputMode: 'decimal',
                  }}
                  sx={{
                    '& .MuiInputBase-input': { typography: 'h3', fontWeight: 700 },
                    '& .MuiOutlinedInput-root': { borderRadius: 2 },
                  }}
                  type="number"
                  error={!!errors.amount}
                  helperText={errors.amount ? errors.amount.message : ''}
                />
              )}
            />

            <Box
              data-testid="transfer-estimate"
              sx={{ mt: 2, px: 2, py: 1.5, borderRadius: 2, bgcolor: 'grey.100' }}
            >
              <Typography>{estimateText}</Typography>
            </Box>
          </DialogContent>
        </form>
      </BaseRecordDialog>
      {pendingData && destination && (
        <TransferConfirmDialog
          open
          crossFarm={isCrossFarm}
          loading={createShrimpTransferMutation.isLoading}
          from={{ pondName: sourcePond?.name ?? '', farmName: currentFarmName }}
          to={{
            pondName: destination.pondName,
            farmName: destination.farmName,
            isCurrentFarm: !isCrossFarm,
          }}
          summary={t('transfer.confirmCrossSummary', {
            kg: formatKg(Number(pendingData.amount)),
            amount: estimate ? fInteger(estimate.amount) : '-',
            value: estimate ? fInteger(estimate.value) : '-',
            pond: destination.pondName,
          })}
          onCancel={() => setPendingData(null)}
          onConfirm={() => submitTransfer(pendingData)}
        />
      )}
    </>
  )
}
export default ShrimpTransferRecordDialog
